import { useMemo } from "react";
import { ColumnDimension } from "@components/common/data-grid/adm-data-grid-types";

import {
  WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS,
  WorkspaceProjectsTableColumnField,
  WorkspaceProjectsTableGridColDef,
} from "@pages/workspace-profile/tab-contents/workspace-projects-tab/workspace-projects-table-types";
import { isCompanyContext } from "@utils/type-guards";
import {
  ProjectArchivedStateCell,
  ProjectAreaCell,
  ProjectCreatedAtCell,
  ProjectGroupsCell,
  ProjectIdCell,
  ProjectModifiedAtCell,
  ProjectNameCell,
  ProjectOptionsCell,
  ProjectStorageCell,
  StandaloneCell,
} from "@utils/data-grid-utils/projects-data-grid-cells";
import { UiUtils } from "@stellar/utils";

const columnDimension: ColumnDimension<WorkspaceProjectsTableColumnField> = {
  name: {
    flex: 1.5,
  },
  id: {
    flex: 1.5,
  },
  area: {
    flex: 0.75,
  },
  storageSize: {
    flex: 0.75,
  },
  archived: {
    flex: 1,
  },
  group: {
    flex: 1,
  },
  createdAt: {
    flex: 1.5,
  },
  modifiedAt: {
    flex: 1.5,
  },
  standalone: {
    flex: 0.5,
  },
  options: {
    flex: 1,
    maxWidth: 120,
  },
};

/**
 * @returns the columns definition to be used in the workspace's projects table
 */
export function useWorkspaceProjectsTableColumns(): WorkspaceProjectsTableGridColDef[] {
  const columnsMap: Record<
    WorkspaceProjectsTableColumnField,
    WorkspaceProjectsTableGridColDef
  > = useMemo(() => {
    return {
      name: {
        field: "name",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.name,
        ...columnDimension.name,
        valueGetter: (_, row) => row.entity.name,
        renderCell: ProjectNameCell,
      },

      id: {
        field: "id",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.id,
        ...columnDimension.id,
        valueGetter: (_, row) => row.entity.id,
        renderCell: ProjectIdCell,
      },

      area: {
        field: "area",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.area,
        ...columnDimension.area,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.area.amount,
        renderCell: ProjectAreaCell,
      },

      storageSize: {
        field: "storageSize",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.storageSize,
        ...columnDimension.storageSize,
        valueGetter: (_, row) => row.entity.storage?.size,
        renderCell: ProjectStorageCell,
      },

      archived: {
        field: "archived",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.archived,
        ...columnDimension.archived,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.state,
        renderCell: ProjectArchivedStateCell,
      },

      group: {
        field: "group",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.group,
        ...columnDimension.group,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) =>
          isCompanyContext(row.entity.context)
            ? row.entity.context.group.name
            : UiUtils.DASH,
        renderCell: ProjectGroupsCell,
      },

      createdAt: {
        field: "createdAt",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.createdAt,
        ...columnDimension.createdAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.createdAt,
        renderCell: ProjectCreatedAtCell,
      },

      modifiedAt: {
        field: "modifiedAt",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.modifiedAt,
        ...columnDimension.modifiedAt,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.modifiedAt,
        renderCell: ProjectModifiedAtCell,
      },

      standalone: {
        field: "standalone",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.standalone,
        ...columnDimension.standalone,
        getApplyQuickFilterFn: () => null,
        valueGetter: (_, row) => row.entity.standalone,
        renderCell: StandaloneCell,
      },

      options: {
        field: "options",
        type: "actions",
        headerName: WORKSPACE_PROJECTS_TABLE_COLUMN_LABELS.options,
        ...columnDimension.options,
        align: "right",
        getApplyQuickFilterFn: () => null,
        renderCell: (params) =>
          ProjectOptionsCell(params, "workspace-projects-table"),
      },
    };
  }, []);

  return Object.values(columnsMap);
}
